// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Images, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ProductImageBatch,
  type ProductImageBatchesSortBy,
  useProductImageBatchesQuery,
  usePublishProductImageBatchMutation,
} from "@/api/hooks/use-product-images";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { ImportStatusBadge } from "@/components/shared/import-status-badge";
import { RowId } from "@/components/shared/row-id";
import { StoreLink } from "@/components/shared/store-link";
import { TimezoneFooterNote } from "@/components/shared/timezone-footer-note";
import { UserLink } from "@/components/shared/user-link";
import { Button } from "@/components/ui/button";
import { BatchProgress } from "@/features/ingress/product-images/batch-progress";
import { UploadProductImagesDialog } from "@/features/ingress/product-images/upload-product-images-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/ingress/product-images/index";

const BATCH_STATUSES = ["processing", "finished", "error"] as const;

// A publish job that has not reported for this long is considered dead; the backend uses the same limit.
const PUBLISH_HEARTBEAT_STALE_MS = 5 * 60 * 1000;

type BatchPhase = "uploading" | "publishing" | "resizing" | null;

/** Where a processing batch is in the pipeline; `null` once it is finished. */
function batchPhase(batch: ProductImageBatch): BatchPhase {
  if (batch.status !== "processing") return null;
  if (batch.publishFinishedAt) return "resizing";
  if (batch.publishStartedAt) return "publishing";
  return "uploading";
}

/** A processing batch whose publish job never ran or stopped reporting can be published (again). */
function canPublish(batch: ProductImageBatch): boolean {
  if (batch.status !== "processing" || batch.publishFinishedAt) return false;
  if (!batch.publishHeartbeatAt) return true;
  return Date.now() - new Date(batch.publishHeartbeatAt).getTime() > PUBLISH_HEARTBEAT_STALE_MS;
}

const FILTER_DEFAULTS = {
  search: "",
  chain: "",
  status: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "importRequestId",
  sortDesc: true,
};

export function ProductImagesList() {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.ingressProductImagesEdit);
  const navigate = useNavigate();
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [statusFilter, setStatusFilter] = useState(initial.status);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Only a click on the refresh button spins it; the background poll stays silent
  const [manualRefresh, setManualRefresh] = useState(false);
  const { mutate: publish, isPending: publishing } = usePublishProductImageBatchMutation();

  const sortBy = (sorting[0]?.id ?? "importRequestId") as ProductImageBatchesSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);
  const isFiltered = !!(search || chainFilter || statusFilter);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setChainFilter("");
    setStatusFilter("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: uploadOpen });

  const currentFilters = useMemo(
    () => ({
      search,
      chain: chainFilter,
      status: statusFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "importRequestId",
      sortDesc: sorting[0]?.desc ?? true,
    }),
    [search, chainFilter, statusFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      const encoded = encodeFilters(currentFilters, FILTER_DEFAULTS);
      navigate({ to: "/ingress/product-images", search: encoded ? { f: encoded } : {}, replace: true });
    },
    [currentFilters, navigate],
  );

  const { data, isLoading, refetch } = useProductImageBatchesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    chainName: chainFilter || undefined,
    status: statusFilter || undefined,
    pollWhileProcessing: true,
  });

  const chainDisplayByName = useMemo(
    () => new Map((context?.chains ?? []).map((chain) => [chain.value, chain.display])),
    [context?.chains],
  );
  const userById = useMemo(() => new Map((data?.users ?? []).map((user) => [user.userId, user])), [data?.users]);

  const columns = useMemo<DataTableColumnDef<ProductImageBatch>[]>(
    () => [
      {
        accessorKey: "importRequestId",
        header: t("productImages.list.columns.id"),
        size: 100,
        enableHiding: false,
        meta: { columnLabel: t("productImages.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.importRequestId} />,
      },
      {
        id: "chain",
        header: t("productImages.list.columns.chain"),
        enableSorting: false,
        meta: { columnLabel: t("productImages.list.columns.chain") },
        cell: ({ row }) =>
          row.original.chainName ? (
            <ChainLink
              chainName={row.original.chainName}
              legibleName={chainDisplayByName.get(row.original.chainName) ?? row.original.chainName}
            />
          ) : (
            "—"
          ),
      },
      {
        id: "referenceStore",
        header: t("productImages.list.columns.referenceStore"),
        enableSorting: false,
        meta: { columnLabel: t("productImages.list.columns.referenceStore") },
        cell: ({ row }) =>
          row.original.referenceStoreName ? (
            <StoreLink
              storeName={row.original.referenceStoreName}
              legibleName={row.original.referenceStoreLegibleName}
            />
          ) : (
            "—"
          ),
      },
      {
        id: "files",
        header: t("productImages.list.columns.files"),
        enableSorting: false,
        meta: { columnLabel: t("productImages.list.columns.files") },
        cell: ({ row }) => <span className="tabular-nums">{row.original.summary.total}</span>,
      },
      {
        id: "progress",
        header: t("productImages.list.columns.progress"),
        enableSorting: false,
        meta: { columnLabel: t("productImages.list.columns.progress") },
        cell: ({ row }) => <BatchProgress batch={row.original} />,
      },
      {
        id: "createdBy",
        header: t("productImages.list.columns.createdBy"),
        enableSorting: false,
        meta: { columnLabel: t("productImages.list.columns.createdBy") },
        cell: ({ row }) => {
          const user = row.original.userId != null ? userById.get(row.original.userId) : undefined;
          if (!user) return "—";
          return <UserLink publicId={user.publicId} legibleName={user.fullName || user.email} />;
        },
      },
      {
        accessorKey: "createdAt",
        header: t("productImages.list.columns.createdAt"),
        meta: { columnLabel: t("productImages.list.columns.createdAt") },
        cell: ({ row }) => <DateTimeValue value={row.original.createdAt ?? null} />,
      },
      {
        accessorKey: "status",
        header: t("productImages.list.columns.status"),
        enableHiding: false,
        meta: { columnLabel: t("productImages.list.columns.status") },
        cell: ({ row }) => {
          const phase = batchPhase(row.original);
          return (
            <span className="inline-flex items-center gap-2">
              <ImportStatusBadge
                status={row.original.status ?? undefined}
                detail={phase ? t(`productImages.list.phase.${phase}`) : undefined}
              />
              {canEdit && canPublish(row.original) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={publishing}
                  onClick={() => publish(row.original.importRequestId)}
                >
                  {t("productImages.list.publishButton")}
                </Button>
              )}
            </span>
          );
        },
      },
    ],
    [t, chainDisplayByName, userById, canEdit, publish, publishing],
  );

  return (
    <div>
      <PageHeader title={t("productImages.title")} />
      <div className="p-6">
        <DataTable
          storageKey="product-image-batches"
          columns={columns}
          data={data?.batches ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={() => {
            setManualRefresh(true);
            void refetch().finally(() => setManualRefresh(false));
          }}
          isRefreshing={manualRefresh}
          footerNote={<TimezoneFooterNote />}
          searchPlaceholder={t("productImages.list.searchPlaceholder")}
          filters={
            <>
              <FilterSelect
                label={t("productImages.list.filter.chain")}
                value={chainFilter}
                onChange={(value) => {
                  setChainFilter(value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("stores.form.searchChains")}
                emptyText={t("stores.form.noChains")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("productImages.list.filter.status")}
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={BATCH_STATUSES.map((value) => ({ label: value, value }))}
                searchPlaceholder={t("stores.operations.searchOption")}
                emptyText={t("stores.operations.noOption")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            canEdit ? (
              <Button onClick={() => setUploadOpen(true)}>
                <Images className="h-4 w-4" />
                {t("productImages.list.uploadButton")}
              </Button>
            ) : null
          }
        />
      </div>

      <UploadProductImagesDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
